import { Box, Grid } from "@mui/material";
import * as React from "react";
import { Base } from "ui/Scripts/source/framework/base";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "../../../framework/appUtils";
import { infiniteListPageSize } from "../../../models/common/webConsts";
import * as userGroupService from "../../../models/services/userGroupService";
import { ISessionState } from "../../../models/session/sessionState";
import { IUserGroupListItem } from "ui/Scripts/source/models/userGroup/userGroupListItem";
import { IUserGroupListItems } from "ui/Scripts/source/models/userGroup/userGroupListItems";
import { ActionContainer } from "../../framework/ActionContainer";
import { AddButton } from "../../framework/Button/AddButton";
import { SettingsButton } from "../../framework/Button/SettingsButton";
import { GridRow } from "../../framework/Grid/GridRow";
import { IListUsingComponentState, List } from "../../framework/List/List";
import { ListActionButtons } from "../../framework/List/ListActionButtons";
import { ListCardField } from "../../framework/List/ListCardField";
import { MainListContainer } from "source/components/framework/List/MainListContainer";
import { ListDetailsButton } from "../../framework/List/ListDetailsButton";
import { ListLinkValue } from "../../framework/List/ListLinkValue";
import { ListSearchContainer } from "../../framework/List/ListSearchContainer";
import { ListSearchFilter } from "../../framework/List/ListSearchFilter";
import { ListToggleButtons } from "../../framework/List/ListToggleButtons";
import { PageTitle } from "../../framework/Page/PageTitle";
import { EllipsisTypography } from "../../framework/Typography/EllipsisTypography";
import { UserGroupDialogMenuItem, UserGroupEditDialog } from "../UserGroupEditDialog";
import { IPageNavMenuItemActionsAction } from "source/components/framework/PageNavMenuItem/Actions";
import { AppAddIcon } from "source/components/framework/Icon/AppAddIcon";
import { AppQrCodeIcon } from "source/components/framework/Icon/AppQrCodeIcon";

// UserGroupList
export interface IUserGroupListProp {
    filterName: string;
    sessionState: ISessionState;
    companyId: string;
    disableGlobalLoading?: boolean;
    preloaded?: boolean;
    waitingPreloadedData?: boolean;
    list?: IUserGroupListItems;
    listFilterIsSet: boolean;
    listFilterIsQrCodeFilter: boolean;
    authorizationLevelIds?: string[];
    dataId?: string;
    sortColumn: string;
    sortOrderIsAsc: boolean;
    reportTabs: React.ReactNode;
    settingsActions: IPageNavMenuItemActionsAction[];
    onRefreshSessionState: () => void;
    onSetSortData: (sortColumn: string, sortOrderIsAsc: boolean) => void;
    onOpenFiltersDialog: () => void;
    onToggleSettingsDialog: () => void;
    onChangeToQrCodeFilter?: () => void;
}

interface IUserGroupListState extends IListUsingComponentState<IUserGroupListItem> {
    // List
    intialized: boolean;
    isLoading: boolean;
    waitingPreloadedData: boolean;
    pageSize: number;
    page: number;
    filter: string;
    authorizationLevels: number[];
    sortColumn: string;
    sortOrderIsAsc: boolean;
    listModeIsLine: boolean;
    checkedIds: string[];

    // Dialog
    editDialogDefaultMenuItem: UserGroupDialogMenuItem;
    editDialogQrCodeUserGroup: boolean;
    editDialogChangeToQrCodeFilter: boolean;
}

export class UserGroupList extends React.Component<IUserGroupListProp, IUserGroupListState> {
    constructor(props: IUserGroupListProp) {
        super(props);
        this.state = {
            intialized: props.list && !!props.preloaded,
            isLoading: false,
            waitingPreloadedData: !Base.isNullOrUndefined(props.waitingPreloadedData) ? props.waitingPreloadedData : (props.list ? false : props.preloaded),
            page: props.list ? props.list.page : 1,
            hasMore: props.list ? props.list.hasMore : true,
            filter: "",
            authorizationLevels: props.authorizationLevelIds ? props.authorizationLevelIds.filter(i => i !== "").map(i => parseInt(i, 10)) : [],
            pageSize: props.list ? props.list.pageSize : infiniteListPageSize,
            sortColumn: props.sortColumn,
            sortOrderIsAsc: props.sortOrderIsAsc,
            listModeIsLine: true,
            items: props.list ? props.list.items : [],
            selectedId: null,
            checkedIds: [],
            detailsItem: null,
            showEditDialog: false,
            editDialogId: "",
            editDialogHasPrevious: false,
            editDialogHasNext: false,
            editDialogDefaultMenuItem: UserGroupDialogMenuItem.General,
            editDialogChangeToQrCodeFilter: false,
            editDialogQrCodeUserGroup: false
        };
    }

    searchItems = async (companyId: string, pageSize: number, page: number, filter: string, authorizationLevels: number[], sortColumn: string, sortOrderIsAsc: boolean,
        resetItems: boolean, refreshList: boolean, moveNext: boolean = false, setDialogData: boolean = true) => {
        const state = this.state;
        this.setState({ isLoading: true });
        const result = await AppUtils.callService(() => userGroupService.getUserGroupListItems(companyId, !refreshList ? pageSize : (page * pageSize), !refreshList ? page : 1, filter, authorizationLevels, sortColumn, sortOrderIsAsc),
            false, this.props.disableGlobalLoading, () => this.setState({ isLoading: false }));
        if (!result) return;
        const items = List.getListItems(state.items, state.selectedId, state.checkedIds, result.items, resetItems, refreshList);
        let selectedId = items.selectedId;
        let index = items.items.findIndex(i => i.id === selectedId);
        let detailsItem = index > -1 ? items.items[index] : null;
        if (moveNext && detailsItem && index < items.items.length - 1) {
            index = index + 1;
            detailsItem = items.items[index];
            selectedId = items.items[index].id;
        }
        if (state.editDialogChangeToQrCodeFilter) {
            setDialogData = false;
        }
        this.setState({
            intialized: true,
            editDialogChangeToQrCodeFilter: false,
            page: !refreshList ? result.page : page,
            hasMore: result.hasMore,
            filter,
            authorizationLevels,
            sortColumn,
            sortOrderIsAsc,
            items: items.items,
            selectedId,
            checkedIds: items.checkedIds,
            detailsItem,
            editDialogId: setDialogData ? (moveNext && detailsItem ? detailsItem.id : "") : this.state.editDialogId,
            editDialogHasPrevious: setDialogData ? index > 0 : this.state.editDialogHasPrevious,
            editDialogHasNext: setDialogData ? (index < items.items.length - 1 || result.hasMore) : this.state.editDialogHasNext,
            editDialogDefaultMenuItem: setDialogData ? UserGroupDialogMenuItem.General : this.state.editDialogDefaultMenuItem
        });
        this.props.onSetSortData(sortColumn, sortOrderIsAsc);
    };

    refreshList = async (setUserGroupDialogData: boolean = true) => {
        const state = this.state;
        await this.searchItems(this.props.companyId, state.pageSize, state.page, state.filter, state.authorizationLevels, state.sortColumn, state.sortOrderIsAsc, false, true, false, setUserGroupDialogData);
    };

    componentDidMount(): void {
        const state = this.state;
        if (this.props.preloaded) return;
        this.searchItems(this.props.companyId, state.pageSize, state.page, state.filter, state.authorizationLevels, state.sortColumn, state.sortOrderIsAsc, false, false);
    }

    componentDidUpdate(prevProps: IUserGroupListProp, prevState: IUserGroupListState) {
        const props = this.props;
        const state = this.state;
        if (state.waitingPreloadedData) {
            if (!props.list || (props.dataId ?? "") === (prevProps.dataId ?? "")) return;
            this.setState({
                intialized: true,
                waitingPreloadedData: false,
                page: props.list.page,
                hasMore: props.list.hasMore,
                items: props.list.items,
                pageSize: props.list.pageSize,
                sortColumn: props.list.sortColumn,
                sortOrderIsAsc: props.list.sortOrderIsAsc
            });
        } else {
            const prevDataId = prevProps.dataId ? prevProps.dataId : "";
            const dataId = props.dataId ? props.dataId : "";
            const prevCompanyId = prevProps.companyId ? prevProps.companyId : "";
            const companyId = props.companyId ? props.companyId : "";
            const prevAuthorizationLevels = prevProps.authorizationLevelIds ? prevProps.authorizationLevelIds.filter(i => i !== "").map(i => parseInt(i, 10)) : [];
            const authorizationLevels = props.authorizationLevelIds ? props.authorizationLevelIds.filter(i => i !== "").map(i => parseInt(i, 10)) : [];
            const prevSortColumn = prevProps.sortColumn;
            const sortColumn = props.sortColumn;
            const prevSortOrderIsAsc = prevProps.sortOrderIsAsc;
            const sortOrderIsAsc = props.sortOrderIsAsc;
            if (dataId === prevDataId && companyId === prevCompanyId &&
                Base.equalArrays(prevAuthorizationLevels, authorizationLevels) &&
                prevSortColumn === sortColumn && prevSortOrderIsAsc === sortOrderIsAsc) return;
            this.searchItems(companyId, state.pageSize, state.page, state.filter, authorizationLevels, sortColumn, sortOrderIsAsc, true, false);
        }
    }

    changeFilter = (filter: string) => {
        const state = this.state;
        this.searchItems(this.props.companyId, state.pageSize, 1, filter, state.authorizationLevels, state.sortColumn, state.sortOrderIsAsc, true, false);
    };

    handleSearchLines = (sortColumn: string, sortOrderIsAsc: boolean, nextPage: boolean) => {
        const state = this.state;
        this.searchItems(this.props.companyId, state.pageSize, !nextPage ? 1 : state.page + 1, state.filter, state.authorizationLevels, sortColumn, sortOrderIsAsc, !nextPage, false);
    };

    handleSetSelectedIds = (selectedId: string, checkedIds: string[]) => {
        this.setState({
            selectedId,
            checkedIds,
            detailsItem: this.state.items.find(i => i.id === selectedId)
        });
    };
    
    handleListModeChange = (listModeIsLine: boolean) => {
        this.setState({
            listModeIsLine,
        });
    };

    handleAdd = () => {
        this.setState({ ...List.getShowEditDialogStateChange(this.state, Base.emptyGuid), editDialogDefaultMenuItem: UserGroupDialogMenuItem.General, editDialogQrCodeUserGroup: false });
    };

    handleAddQrCodeUserGroup = () => {
        this.setState({ ...List.getShowEditDialogStateChange(this.state, Base.emptyGuid), editDialogDefaultMenuItem: UserGroupDialogMenuItem.General, editDialogQrCodeUserGroup: true });
    };

    handleOpenLine = (id: string) => {
        this.setState({ ...List.getShowEditDialogStateChange(this.state, id), editDialogDefaultMenuItem: UserGroupDialogMenuItem.General });
    };

    handleUserGroupDialogOk = () => {
        this.refreshList(false);
    };

    handleUserGroupDialogCancel = (userDataWasModified: boolean) => {
        this.setState({
            showEditDialog: false
        });
        if (!userDataWasModified) return;
        this.refreshList(false);
    };

    handleUserGroupDialogNewSaved = (newUserGroupId: string) => {
        const editDialogChangeToQrCodeFilter = this.state.editDialogQrCodeUserGroup && !this.props.listFilterIsQrCodeFilter && !!this.props.onChangeToQrCodeFilter;
        this.setState({
            showEditDialog: true,
            selectedId: newUserGroupId,
            editDialogId: newUserGroupId,
            editDialogHasPrevious: false,
            editDialogHasNext: false,
            editDialogDefaultMenuItem: UserGroupDialogMenuItem.Users,
            editDialogChangeToQrCodeFilter
        });
        if (editDialogChangeToQrCodeFilter) {
            this.props.onChangeToQrCodeFilter();
        } else {
            this.refreshList(false);
        }
    };

    handleListModeToggle = (_event: React.MouseEvent<HTMLElement>, listModeIsLine: boolean) => {
        this.setState({
            listModeIsLine,
        });
    };

    setEditDialogItemByIndex = (index: number) => {
        const state = this.state;
        this.setState(List.getEditDialogItemByIndexStateChange(this.state, index, () => this.searchItems(this.props.companyId, state.pageSize, state.page + 1, state.filter, state.authorizationLevels, state.sortColumn, state.sortOrderIsAsc, false, false, true)));
    };

    render() {
        const props = this.props;
        const state = this.state;
        const isSuperAdmin = props.sessionState.isSuperAdmin;
        return (
            <>
                <PageTitle title={Translations.UserGroups + (props.filterName ? " - " + props.filterName : (props.listFilterIsSet || state.filter ? " - " + Translations.Filtered : ""))}>
                    {props.settingsActions.length > 0 &&
                        <SettingsButton onClick={props.onToggleSettingsDialog} sx={{ marginRight: "1.5rem" }}>
                            {Translations.Settings}
                        </SettingsButton>
                    }
                    <AddButton
                        onClick={this.handleAdd}
                        actions={[
                            { title: Translations.AddUserGroup, icon: <AppAddIcon />, onClick: this.handleAdd },
                            { title: Translations.AddQrCodeUserGroup, icon: <AppQrCodeIcon />, onClick: this.handleAddQrCodeUserGroup }
                        ]}>
                        {Translations.Add}
                    </AddButton>
                </PageTitle>
                <MainListContainer>
                    <ActionContainer
                        margin="1rem .5rem 1rem 1rem"
                        leftItems={
                            props.reportTabs
                        }
                        rightItems={
                            <>
                                <ListActionButtons
                                    filterIsSet={props.listFilterIsSet}
                                    onFilter={props.onOpenFiltersDialog}
                                />
                                <ListToggleButtons value={state.listModeIsLine} onChange={this.handleListModeToggle} />
                            </>
                        }
                    />
                    <ListSearchContainer>
                        <ListSearchFilter
                            searchFilter={state.filter}
                            onSearchClick={this.changeFilter} />
                    </ListSearchContainer>
                    <List
                        intialized={state.intialized}
                        isLoading={state.isLoading}
                        hasMore={state.hasMore}
                        height="calc(100vh - 296px)" //Main List
                        globalLoadingInUse={!props.disableGlobalLoading}
                        header={state.listModeIsLine
                            ? {
                                columns: isSuperAdmin
                                    ? [
                                        { size: 3, column: "code", content: Translations.Code },
                                        { size: 3, column: "name", content: Translations.Name },
                                        { size: 4, column: "comment", content: Translations.Description },
                                        { size: 2, column: "authorizationLevel", content: Translations.AuthorizationLevel }
                                    ]
                                    : [
                                        { size: 4, column: "name", content: Translations.Name },
                                        { size: 6, column: "comment", content: Translations.Description },
                                        { size: 2, column: "authorizationLevel", content: Translations.AuthorizationLevel }
                                    ]
                            }
                            : null}
                        lines={state.listModeIsLine
                            ? state.items.map((item) => {
                                return {
                                    columns: isSuperAdmin
                                        ? [
                                            { size: 3, column: "code", content: <ListLinkValue onClick={() => this.handleOpenLine(item.id)}>{item.code}</ListLinkValue> },
                                            { size: 3, column: "name", content: item.name },
                                            { size: 4, column: "comment", content: item.comment },
                                            { size: 2, column: "authorizationLevel", content: item.levelStr }
                                        ]
                                        : [
                                            { size: 4, column: "name", content: <ListLinkValue onClick={() => this.handleOpenLine(item.id)}>{item.name}</ListLinkValue> },
                                            { size: 6, column: "comment", content: item.comment },
                                            { size: 2, column: "authorizationLevel", content: item.levelStr }
                                        ],
                                    id: item.id
                                };
                            })
                            : null
                        }
                        cards={!state.listModeIsLine
                            ? state.items.map((item) => {
                                return {
                                    sm: 4,
                                    rows: [
                                        {
                                            cols: isSuperAdmin
                                                ? [
                                                    { size: 6, title: Translations.Code, value: <Box display="flex" overflow="hidden"><ListLinkValue onClick={() => this.handleOpenLine(item.id)}>{item.code}</ListLinkValue></Box> },
                                                    { size: 6, title: Translations.Name, value: item.name }
                                                ]
                                                : [
                                                    { size: 6, title: Translations.Name, value: <ListLinkValue onClick={() => this.handleOpenLine(item.id)}>{item.name}</ListLinkValue> },
                                                    { size: 6, title: Translations.AuthorizationLevel, value: <EllipsisTypography>{item.levelStr}</EllipsisTypography> }
                                                ]
                                        },
                                        isSuperAdmin
                                            ? {
                                                cols: [
                                                    { size: 6, title: Translations.AuthorizationLevel, value: <EllipsisTypography>{item.levelStr}</EllipsisTypography> },
                                                ]
                                            }
                                            : null,
                                        {
                                            cols: [
                                                { title: Translations.Description, value: item.comment },
                                            ]
                                        },
                                        {
                                            cols: [
                                                {
                                                    title: Translations.Users,
                                                    value:
                                                    <EllipsisTypography title={Base.getStringWithSeparators(item.users.map(i => i.name), ", ")}>
                                                        {item.users.map((user, index) =>
                                                            <span key={user.id}>
                                                                {index > 0 &&
                                                                    <span>,&nbsp;</span>
                                                                }
                                                                <span className={user.disabled ? "notInUse" : null}>{user.name}</span>
                                                            </span>
                                                        )}
                                                    </EllipsisTypography>
                                                }
                                            ]
                                        }],
                                    id: item.id
                                };
                            })
                            : null
                        }
                        lineDetails={state.detailsItem
                            ? <Box position="relative">
                                <GridRow>
                                    <Grid item sm={4}>
                                        {isSuperAdmin &&
                                        <GridRow>
                                            <Grid item sm={12}>
                                                <ListCardField title={Translations.Code} value={state.detailsItem.code} />
                                            </Grid>
                                        </GridRow>
                                        }
                                        <GridRow>
                                            <Grid item sm={12}>
                                                <ListCardField title={Translations.Name} value={state.detailsItem.name} />
                                            </Grid>
                                        </GridRow>
                                        <GridRow>
                                            <Grid item sm={12}>
                                                <ListCardField title={Translations.AuthorizationLevel} value={state.detailsItem.levelStr} />
                                            </Grid>
                                        </GridRow>
                                    </Grid>
                                    <Grid item sm={4}>
                                        <GridRow>
                                            <Grid item sm={12}>
                                                <ListCardField title={Translations.Description} value={state.detailsItem.comment} />
                                            </Grid>
                                        </GridRow>
                                    </Grid>
                                    <Grid item sm={4}>
                                        <GridRow>
                                            <Grid item sm={12}>
                                                <ListCardField title={Translations.Users} value={
                                                    <div className="value canWrap" title={Base.getStringWithSeparators(state.detailsItem.users.map(i => i.name), ", ")}>
                                                        {state.detailsItem.users.map((user, index) =>
                                                            (index < 10
                                                                ? <span key={user.id}>
                                                                    {index > 0 &&
                                                                <span>,&nbsp;</span>
                                                                    }
                                                                    <span className={user.disabled ? "notInUse" : null}>{user.name}</span>
                                                                </span>
                                                                : null
                                                            )
                                                        )}
                                                        {state.detailsItem.users.length > 6 &&
                                                    <span>{" + (" + (state.detailsItem.users.length - 10).toString(10) + ")"}</span>
                                                        }
                                                    </div>
                                                } />
                                            </Grid>
                                        </GridRow>
                                    </Grid>
                                </GridRow>
                                <ListDetailsButton
                                    onClick={() => this.handleOpenLine(state.selectedId)}
                                />
                            </Box>
                            : null}
                        sortColumn={state.sortColumn}
                        sortOrderIsAsc={state.sortOrderIsAsc}
                        selectedId={state.selectedId}
                        checkedIds={state.checkedIds}
                        onSetSelectedIds={this.handleSetSelectedIds}
                        onSearchLines={this.handleSearchLines}
                        onOpenLine={this.handleOpenLine}
                    />
                </MainListContainer>
                {state.showEditDialog &&
                    <UserGroupEditDialog
                        sessionState={props.sessionState}
                        companyId={props.companyId}
                        defaultMenuItem={state.editDialogDefaultMenuItem}
                        qrCodeUserGroup={state.editDialogQrCodeUserGroup}
                        userGroupId={state.editDialogId}
                        hasPrevious={state.editDialogHasPrevious}
                        hasNext={state.editDialogHasNext}
                        onSaved={this.handleUserGroupDialogOk}
                        onClose={this.handleUserGroupDialogCancel}
                        onRefreshList={() => this.refreshList(false)}
                        onNewSaved={this.handleUserGroupDialogNewSaved}
                        onPrevious={() => List.moveToPrevious(state, this.setEditDialogItemByIndex)}
                        onNext={() => List.moveToNext(state, this.setEditDialogItemByIndex)}
                        onRefreshSessionState={props.onRefreshSessionState}
                    />
                }
            </>
        );
    }
}